/**
 * MissingModelHero — Recovery state when source exists but no business model.
 *
 * Guides user to regenerate the business model in one click.
 */

import React from 'react';
import {
  Text,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle20Filled,
  Circle20Regular,
  TableLink20Regular,
  PlugConnected20Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    ...shorthands.padding('28px', '32px'),
    ...shorthands.borderRadius('12px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    boxShadow: tokens.shadow2,
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '20px',
  },
  statusList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '13px',
    fontWeight: 500,
  },
  statusDone: {
    color: tokens.colorStatusSuccessForeground1,
  },
  statusPending: {
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    marginTop: '4px',
  },
});

interface MissingModelHeroProps {
  onGenerateModel: () => void;
  onViewSource: () => void;
}

const MissingModelHero: React.FC<MissingModelHeroProps> = ({ onGenerateModel, onViewSource }) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <Text className={styles.title}>Your data is connected</Text>
      <Text className={styles.subtitle}>
        Your business model is missing. Generate one to start creating dashboards and exploring your data.
      </Text>

      <div className={styles.statusList}>
        <div className={`${styles.statusItem} ${styles.statusDone}`}>
          <CheckmarkCircle20Filled />
          <span>Data source connected</span>
        </div>
        <div className={`${styles.statusItem} ${styles.statusPending}`}>
          <Circle20Regular />
          <span>Business model missing</span>
        </div>
        <div className={`${styles.statusItem} ${styles.statusPending}`}>
          <Circle20Regular />
          <span>Dashboards unavailable</span>
        </div>
      </div>

      <div className={styles.actions}>
        <Button appearance="primary" icon={<TableLink20Regular />} onClick={onGenerateModel}>
          Generate Business Model
        </Button>
        <Button appearance="outline" icon={<PlugConnected20Regular />} onClick={onViewSource}>
          View Source
        </Button>
      </div>
    </div>
  );
};

export default MissingModelHero;